import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Menu, ChevronDown } from 'lucide-react';
import DemoLink from '@/components/DemoLink';

const SPORT_NAV = [
  { label: 'Soccer', emoji: '⚽', slug: 'soccer' },
  { label: 'Basketball', emoji: '🏀', slug: 'basketball' },
  { label: 'Baseball', emoji: '⚾', slug: 'baseball' },
  { label: 'Softball', emoji: '🥎', slug: 'softball' },
  { label: 'Flag Football', emoji: '🏈', slug: 'flag-football' },
] as const;

const Navbar = () => {
  const { user, signOut } = useAuth();
  const { isAdmin, isImpersonating } = useAdmin();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const showDashboard = !isAdmin || isImpersonating;

  return (
    <nav className="bg-card shadow-sm py-4 border-b">
      <div className="container mx-auto px-4 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-2">
          <img src="/statharbour-logo.svg" alt="StatHarbour" width="160" height="40" className="h-10 w-auto" />
        </Link>
        
        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center space-x-6">
          <Link to="/features" className="text-muted-foreground hover:text-primary transition-colors">Product</Link>
          <Link to="/#match-centre" className="text-muted-foreground hover:text-primary transition-colors">Match Centre</Link>
          <Link to="/#operations" className="text-muted-foreground hover:text-primary transition-colors">Competition Operations</Link>
          <Link to="/pricing" className="text-muted-foreground hover:text-primary transition-colors">Pricing</Link>
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors text-sm">
              Sports <ChevronDown className="w-3.5 h-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              {SPORT_NAV.map(s => (
                <DropdownMenuItem key={s.slug} asChild>
                  <Link to={`/sports/${s.slug}`} className="flex items-center gap-2">
                    <span>{s.emoji}</span>
                    <span>{s.label}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {user && (
            <Link to="/docs" className="text-muted-foreground hover:text-primary transition-colors">Documentation</Link>
          )}
          <Link to="/walkthrough" className="text-muted-foreground hover:text-primary transition-colors">Product Tour</Link>
          <DemoLink className="font-medium text-primary hover:text-primary/80 transition-colors">
            Live Demo
          </DemoLink>
        </div>
        
        {/* Desktop User Menu */}
        <div className="hidden lg:flex items-center space-x-3">
          {user ? (
            <>
              {showDashboard && (
                <Link to="/dashboard">
                  <Button variant="outline" size="sm">Dashboard</Button>
                </Link>
              )}
              <Link to="/profile">
                <Button variant="ghost" size="sm">Profile</Button>
              </Link>
              {isAdmin && (
                <Link to="/admin">
                  <Button variant="ghost" size="sm">Admin</Button>
                </Link>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => signOut()}
              >
                Logout
              </Button>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" size="sm">Sign in</Button>
              </Link>
              <Link to="/signup">
                <Button variant="default" size="sm">Start free</Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile Menu */}
        <div className="lg:hidden">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" aria-label="Open navigation menu">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <div className="flex flex-col space-y-4 mt-8">
                <Link to="/features" className="text-foreground hover:text-primary transition-colors p-2" onClick={() => setMobileMenuOpen(false)}>
                  Product
                </Link>
                <Link to="/#match-centre" className="text-foreground hover:text-primary transition-colors p-2" onClick={() => setMobileMenuOpen(false)}>
                  Match Centre
                </Link>
                <Link to="/#operations" className="text-foreground hover:text-primary transition-colors p-2" onClick={() => setMobileMenuOpen(false)}>
                  Competition Operations
                </Link>
                <Link to="/pricing" className="text-foreground hover:text-primary transition-colors p-2" onClick={() => setMobileMenuOpen(false)}>
                  Pricing
                </Link>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2 mb-1">Sports</p>
                  <div className="flex flex-col">
                    {SPORT_NAV.map(s => (
                      <Link
                        key={s.slug}
                        to={`/sports/${s.slug}`}
                        className="flex items-center gap-2 text-foreground hover:text-primary transition-colors p-2 pl-4 text-sm"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <span>{s.emoji}</span>
                        <span>{s.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
                {user && (
                  <Link to="/docs" className="text-foreground hover:text-primary transition-colors p-2" onClick={() => setMobileMenuOpen(false)}>
                    Documentation
                  </Link>
                )}
                <Link to="/walkthrough" className="text-foreground hover:text-primary transition-colors p-2" onClick={() => setMobileMenuOpen(false)}>
                  Product Tour
                </Link>
                <DemoLink className="text-primary font-medium hover:text-primary/80 transition-colors p-2" onClick={() => setMobileMenuOpen(false)}>
                  Live Demo
                </DemoLink>
                
                <div className="border-t pt-4 space-y-2">
                  {user ? (
                    <>
                      {showDashboard && (
                        <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                          <Button variant="outline" className="w-full justify-start">Dashboard</Button>
                        </Link>
                      )}
                      <Link to="/profile" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="ghost" className="w-full justify-start">Profile</Button>
                      </Link>
                      {isAdmin && (
                        <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                          <Button variant="ghost" className="w-full justify-start">Admin</Button>
                        </Link>
                      )}
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start"
                        onClick={() => {
                          signOut();
                          setMobileMenuOpen(false);
                        }}
                      >
                        Logout
                      </Button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="ghost" className="w-full justify-start">Sign in</Button>
                      </Link>
                      <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="default" className="w-full justify-start">Start free</Button>
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
